import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { LogOut, Users, BookOpen, MessageSquare, Calendar, Microscope } from 'lucide-react';
import { toast } from 'sonner';

import EnquiryManagement from './EnquiryManagement';
import CourseManagement from './CourseManagement';
import BatchManagement from './BatchManagement';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = ({ user }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalEnquiries: 0,
    newEnquiries: 0,
    totalCourses: 0,
    totalBatches: 0
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [enquiriesRes, coursesRes, batchesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/enquiries`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/courses`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/batches`, { withCredentials: true })
      ]);

      setStats({
        totalEnquiries: enquiriesRes.data.length,
        newEnquiries: enquiriesRes.data.filter(e => e.status === 'new').length,
        totalCourses: coursesRes.data.length,
        totalBatches: batchesRes.data.length
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Logout failed');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-purple-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-purple-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <div className="bg-gradient-to-br from-purple-600 to-purple-800 p-2 rounded-lg">
                <Microscope className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-purple-900">Tejeswi Admin</h1>
                <p className="text-xs text-purple-600">Chemistry Education Portal</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-semibold text-gray-900">{user?.name}</p>
                <p className="text-xs text-purple-600">Administrator</p>
              </div>
              <Button onClick={handleLogout} variant="outline" size="sm" className="border-purple-200">
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="border-purple-200">
            <CardHeader className="pb-3">
              <CardDescription className="flex items-center text-purple-700">
                <MessageSquare className="h-4 w-4 mr-2" />
                Total Enquiries
              </CardDescription>
              <CardTitle className="text-3xl text-purple-900">{stats.totalEnquiries}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600">{stats.newEnquiries} new</p>
            </CardContent>
          </Card>

          <Card className="border-purple-200">
            <CardHeader className="pb-3">
              <CardDescription className="flex items-center text-purple-700">
                <BookOpen className="h-4 w-4 mr-2" />
                Total Courses
              </CardDescription>
              <CardTitle className="text-3xl text-purple-900">{stats.totalCourses}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600">Active offerings</p>
            </CardContent>
          </Card>

          <Card className="border-purple-200">
            <CardHeader className="pb-3">
              <CardDescription className="flex items-center text-purple-700">
                <Calendar className="h-4 w-4 mr-2" />
                Total Batches
              </CardDescription>
              <CardTitle className="text-3xl text-purple-900">{stats.totalBatches}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600">Running batches</p>
            </CardContent>
          </Card>

          <Card className="border-purple-200">
            <CardHeader className="pb-3">
              <CardDescription className="flex items-center text-purple-700">
                <Users className="h-4 w-4 mr-2" />
                System Status
              </CardDescription>
              <CardTitle className="text-lg text-green-600">● Active</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600">All systems operational</p>
            </CardContent>
          </Card>
        </div>

        {/* Management Tabs */}
        <Card className="border-purple-200">
          <Tabs defaultValue="enquiries" className="w-full">
            <CardHeader>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="enquiries">Enquiries</TabsTrigger>
                <TabsTrigger value="courses">Courses</TabsTrigger>
                <TabsTrigger value="batches">Batches</TabsTrigger>
              </TabsList>
            </CardHeader>
            <CardContent>
              <TabsContent value="enquiries">
                <EnquiryManagement onUpdate={fetchStats} />
              </TabsContent>
              <TabsContent value="courses">
                <CourseManagement onUpdate={fetchStats} />
              </TabsContent>
              <TabsContent value="batches">
                <BatchManagement onUpdate={fetchStats} />
              </TabsContent>
            </CardContent>
          </Tabs>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
